import React, { useMemo } from "react"
import wrapAmpersand from "design_system/utils/ampersand_wrapper"
import { FiLink } from "react-icons/fi"
import classNames from "classnames"
import { useRemarkSync } from "react-remark"
import rehypeRaw from "rehype-raw"
import RemarkDropcap from "design_system/utils/remark_dropcap"
import CelticDropCap from "design_system/application/components/celtic_drop_cap"

import "./index.css"
import { twMerge } from "tailwind-merge"

export const sizes = {
  xs: "text-base",
  sm: "text-xl",
  md: "text-3xl",
  lg: "text-6xl",
  xl: "text-8xl",
} as const

export const variants = {
  fancy: "font-fancy",
  default: "font-serif",
  plain: "font-sans",
} as const

export type TitleSize = keyof typeof sizes
type DropcapSize = "sm" | "md" | "lg" | "xl"
export type TitleVariant = keyof typeof variants

interface UrlObject {
  hash?: string
}

export function stringFromUrl(
  url: string | UrlObject | undefined
): string | undefined {
  if (typeof url === "string") return url
  if (typeof url === "object" && url.hash) return `#${url.hash}`
}

interface LinkedTitleProps
  extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  showIcon?: boolean
  href?: string | UrlObject
  title: string | undefined | null
  dropcap?: boolean | DropcapSize
  remarkPlugins?: any[]
}

const LinkedTitle = ({
  showIcon = true,
  href,
  title,
  dropcap,
  ...props
}: LinkedTitleProps) => {
  const hrefString = stringFromUrl(href)
  const Component = "a"
  return (
    <Component href={hrefString}>
      <DefaultTitle
        title={title}
        style={{ marginRight: ".3em" }}
        dropcap={dropcap}
        {...props}
      />
      {showIcon && (
        <FiLink
          className="inline text-secondary group-hover:text-gray-400 transition ease-in-out align-middle h4 "
          style={{ fontSize: ".7em" }}
        />
      )}
    </Component>
  )
}

interface TitleProps extends Omit<React.HTMLAttributes<HTMLElement>, "title"> {
  title: string | undefined | null
  className?: string
  dropcap?: boolean | DropcapSize
}

const DefaultTitle = ({
  title,
  className,
  dropcap = false,
  ...props
}: TitleProps) => {
  const remarkPlugins: any[] = []
  if (dropcap) remarkPlugins.push([RemarkDropcap, { renderDropcap: dropcap }])

  const reactContent = useMemo(() => {
    return useRemarkSync(wrapAmpersand(title || ""), {
      remarkPlugins,
      remarkToRehypeOptions: { allowDangerousHtml: true },
      rehypePlugins: [rehypeRaw],
      rehypeReactOptions: {
        components: {
          p: React.Fragment,
          a: React.Fragment,
          span: (props: any) => {
            if (props.className === "dropcap" && dropcap) {
              return (
                <CelticDropCap
                  size={typeof dropcap === "string" ? dropcap : "md"}
                  className="m-0 mr-3"
                  letter={props.children[0].toLowerCase()}
                />
              )
            }

            return <span {...props} />
          },
        },
      },
    })
  }, [title, dropcap])

  return (
    <span className={classNames("align-middle", className)} {...props}>
      {reactContent}
    </span>
  )
}

interface PostTitleProps {
  /**
   * Supports simple markdown (**bold** _italic_) but will not transform links
   */
  title: string | null | undefined
  /**
   * URL or URL object with hash
   */
  href?: string | UrlObject
  /**
   * Size of the title
   */
  size?: TitleSize
  /**
   * Component to render as (h1, h2, p, etc.)
   */
  as?: React.ElementType
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Enable Celtic drop cap on first letter. Pass size string to customize.
   */
  dropcap?: boolean | DropcapSize | null
  /**
   * Show link icon when href is provided
   */
  showIcon?: boolean

  /**
   * Variant for styling (not used in this implementation but can be extended)
   */
  variant?: TitleVariant
}

const Title = ({
  title,
  href,
  size = "md",
  variant,
  as: Component = "p",
  className = "",
  dropcap = null,
  showIcon = true,
  ...props
}: PostTitleProps) => {
  if (!title) return null

  return (
    <Component
      data-testid="title"
      className={twMerge(
        "post-title",
        size && [sizes[size]],
        variant && [variants[variant]],
        className
      )}
      {...props}
    >
      {href ? (
        <LinkedTitle
          title={title}
          href={href}
          dropcap={dropcap ?? false}
          showIcon={showIcon}
        />
      ) : (
        <DefaultTitle title={title} dropcap={dropcap ?? false} />
      )}
    </Component>
  )
}

export default Title
